import type { CampaignState, ItemInstance, PlayerState } from '@somnhold/sim';

/*
 * New pieces in the bag (asked for by players: "light them up, and say who sent it"). A piece is new until the player
 * looks at it: picks it, hovers it, or closes the sheet after the bag was on screen (as bags in most games do). Gifts
 * are also announced once, by who sent them. What was seen is kept per device, by campaign, player and hero: it is
 * the player's view, not the campaign's (no command, nothing for the server to replay).
 */

interface Store {
  /** Pieces this hero has had in view (worn ones count by themselves). */
  known: number[];
  /** Gifts already announced. */
  told: number[];
}

let key = '';
let store: Store | null = null;
/** Pieces new in the bag as the sheet last drew them: closing the sheet counts them as seen. */
const shown = new Set<number>();
const listeners = new Set<() => void>();

const keyOf = (s: CampaignState, p: PlayerState): string => `somnhold.bagSeen.${s.id}.${p.id}.${p.hero.heroClass}`;

const save = (): void => {
  if (!store) return;
  try {
    localStorage.setItem(key, JSON.stringify(store));
  } catch {
    /* private mode: it lives as long as the page */
  }
};

const changed = (): void => {
  for (const f of listeners) f();
};

/**
 * Loads (or starts) this hero's record and keeps it in step with its gear: worn pieces are known, pieces gone from
 * the hero are forgotten (so the record stays the size of a bag). A hero seen here for the first time starts with
 * everything known except the gifts, so a full bag doesn't light up whole the day this comes out, but what friends
 * sent does.
 */
const load = (s: CampaignState, p: PlayerState): Store => {
  const k = keyOf(s, p);
  if (store && key === k) return store;
  key = k;
  shown.clear();
  let got: Store | null = null;
  try {
    const raw = localStorage.getItem(k);
    if (raw) got = JSON.parse(raw) as Store;
  } catch {
    /* unreadable: start again */
  }
  store = got && Array.isArray(got.known) && Array.isArray(got.told) ? got : { known: p.hero.bag.filter((x) => !x.from).map((x) => x.id), told: [] };
  return store;
};

/** Brings the record in step with the hero's gear; true when it changed. */
const tidy = (st: Store, p: PlayerState): boolean => {
  const held = new Set(p.hero.bag.map((x) => x.id));
  const worn = Object.values(p.hero.gear).flatMap((x) => (x ? [x.id] : []));
  for (const id of worn) held.add(id);
  const known = [...new Set([...st.known.filter((id) => held.has(id)), ...worn])];
  const told = st.told.filter((id) => held.has(id));
  const same = known.length === st.known.length && told.length === st.told.length;
  st.known = known;
  st.told = told;
  return !same;
};

/** The bag pieces this hero hasn't looked at yet. */
export const freshIds = (s: CampaignState, p: PlayerState): Set<number> => {
  const st = load(s, p);
  const known = new Set(st.known);
  return new Set(p.hero.bag.filter((x) => !known.has(x.id)).map((x) => x.id));
};

/** Gifts that came and haven't been announced: marked as told (each one is announced once, on this device). */
export const takeGifts = (s: CampaignState, p: PlayerState): ItemInstance[] => {
  const st = load(s, p);
  const dirty = tidy(st, p);
  const known = new Set(st.known);
  const told = new Set(st.told);
  const gifts = p.hero.bag.filter((x) => x.from && !known.has(x.id) && !told.has(x.id));
  if (gifts.length) st.told.push(...gifts.map((x) => x.id));
  if (dirty || gifts.length) save();
  return gifts;
};

/** The player looked at these pieces. */
export const markSeen = (s: CampaignState, p: PlayerState, ids: Iterable<number>): void => {
  const st = load(s, p);
  const known = new Set(st.known);
  let n = 0;
  for (const id of ids) {
    shown.delete(id);
    if (known.has(id)) continue;
    st.known.push(id);
    known.add(id);
    n++;
  }
  if (!n) return;
  save();
  changed();
};

/** The sheet drew these new pieces in the bag. */
export const noteShown = (ids: Iterable<number>): void => {
  shown.clear();
  for (const id of ids) shown.add(id);
};

/** The sheet closed: what it showed in the bag counts as seen. */
export const ackShown = (s: CampaignState, p: PlayerState): void => {
  if (shown.size) markSeen(s, p, [...shown]);
};

/** Called whenever what is new changes (the hero button's count follows it). Returns how to stop. */
export const onFreshChange = (f: () => void): (() => void) => {
  listeners.add(f);
  return () => listeners.delete(f);
};
